/**
 * Live flip: a run status change for one work item reflows whatever the
 * caller passes — no refresh. `runs.updated`/`runs.created` are dispatched as
 * window CustomEvents by useWebSocket, carrying the RunSummary (snake_case
 * `work_item_id`) the backend broadcast. Extracted verbatim from CaseFile
 * (YAG-440) so the review case body listens the same way — never a fork.
 *
 * A null work item id is a surface with nothing open (the Work index rail): it
 * subscribes to nothing rather than making its caller branch around the hook.
 */
import { useEffect } from "react";

export function useRunLiveFlip(workItemId: string | null, onFlip: () => void): void {
  useEffect(() => {
    if (!workItemId) return;
    const onRun = (e: Event) => {
      const run = (e as CustomEvent<{ run?: { work_item_id?: string } }>).detail?.run;
      if (run?.work_item_id !== workItemId) return;
      onFlip();
    };
    window.addEventListener("runs.updated", onRun);
    window.addEventListener("runs.created", onRun);
    return () => {
      window.removeEventListener("runs.updated", onRun);
      window.removeEventListener("runs.created", onRun);
    };
  }, [workItemId, onFlip]);
}
